/**
 * Что «Команда» спрашивает у сервера.
 *
 * Почти всё — готовый API экрана «Пользователи и доступ» (`platform-api`,
 * `routers/access.py`): список людей и заявок, вход в сервисы, должность,
 * права должностей и личные исключения, новая должность. Портал не заводит
 * вторых правил — те же адреса, те же проверки §8.4 на сервере.
 *
 * Сверху — одно своё обращение: флаг денег (D47) в `ops-api`,
 * `/api/ops/portal/money`, через `member.grant_finance`/`revoke_finance`,
 * как кнопка «Доступ к деньгам» в боте.
 *
 * Отказы платформы причину не называют (§6.4) — текст для человека
 * подбирается по коду ответа в `problemText`.
 */

import { ApiError } from '@shell/api';
import { request } from '../../api';

export type ServiceCode = 'ops' | 'finance' | 'study';

export interface TeamUser {
  user_id: number;
  full_name: string | null;
  position_id: number | null;
  position_name: string | null;
  employee_status: string | null;
  is_host: boolean;
  /** Сервис → уровень: `user`, `admin`, у владельца — `host`. Нет ключа — нет входа. */
  access: Record<string, string>;
  /** Может ли копировать сейчас (D233): владелец, «Собственник» или отметка. */
  can_copy?: boolean;
  /** Отметка владельца — то, что переключает кнопка. */
  copy_granted?: boolean;
}

export interface AccessRequest {
  id: number;
  service: string;
  services: string[] | null;
  applicant_name: string | null;
  applicant_note: string | null;
  requested_at: string;
  /** Кто это из справочника (D234): сотрудники без Telegram, совпавшие по имени — первыми. */
  candidates?: RequestCandidate[];
}

export interface RequestCandidate {
  employee_id: number;
  full_name: string;
  position_name: string | null;
  /** `exact` — те же слова имени; `partial` — совпала фамилия или имя; `none` — прочие. */
  match: 'exact' | 'partial' | 'none';
}

export interface PositionOption {
  id: number;
  name: string;
  is_owner: boolean;
  /** Подразделение: «Зал», «Кухня»… `null` — не задано. */
  department?: string | null;
  /**
   * Должность официантская (D247): её показатели сравниваются между
   * собой, и только они уезжают наставнику. Код кассы есть у всех, кто
   * пробивает чеки, — у менеджера, бармена, хостес, — но их чеки это
   * помощь залу, а не их работа.
   */
  is_waiter?: boolean;
}

/** Сотрудник из справочника, ещё ни разу не писавший ботам. */
export interface StaffMember {
  employee_id: number;
  full_name: string;
  position_id: number | null;
  position_name: string | null;
  employee_status: string;
  /** Вызывной, вписанный в табель вручную (D266): кто вписал и когда. */
  on_call?: boolean;
  added_by_name?: string | null;
  added_at?: string | null;
}

/** «вызывной · вписала Иванова Катя 20.10.2026» — одна строка на список и карточку. */
export function onCallNote(member: StaffMember): string | null {
  if (!member.on_call) return null;
  const parts = ['вызывной'];
  const day = member.added_at ? member.added_at.slice(0, 10).split('-').reverse().join('.') : '';
  if (member.added_by_name || day) {
    parts.push(['вписан в табель', member.added_by_name ? `— ${member.added_by_name}` : '', day].filter(Boolean).join(' '));
  }
  return parts.join(' · ');
}

export interface UsersScreen {
  me: { user_id: number };
  services: string[];
  users: TeamUser[];
  requests: AccessRequest[];
  employees_without_account: number;
  /** Сами люди без Telegram — в общем списке «Сотрудники». */
  staff?: StaffMember[];
  positions: PositionOption[];
}

/** Человек на должности — для раскрытия в списке должностей. */
export interface PositionPerson {
  employee_id: number;
  /** Пусто — ещё не писал боту. */
  user_id: number | null;
  full_name: string;
}

/** Куда ведёт человек из любого списка: его карточка. */
export function personPath(person: { user_id: number | null; employee_id: number }): string {
  return person.user_id !== null
    ? `/team/people/${person.user_id}`
    : `/team/staff/${person.employee_id}`;
}

export interface RightObject {
  service: string;
  code: string;
  title: string;
  hint: string;
  actions: string[];
  is_money: boolean;
}

export interface PositionRights extends PositionOption {
  /** Сколько действующих сотрудников на должности. */
  employees?: number;
  /** Кто на должности. */
  people?: PositionPerson[];
  rights: Record<string, { configured: boolean; granted: string[] }>;
}

export interface RightsScreen {
  services: string[];
  objects: RightObject[];
  positions: PositionRights[];
}

export interface UserServiceRights {
  access_level: string | null;
  base: string;
  /** «объект:действие» → откуда: host, owner, admin, position, default, added. */
  granted: Record<string, string>;
  removed: string[];
}

export interface UserRights {
  user_id: number;
  services: Record<string, UserServiceRights>;
}

export interface MoneyPerson {
  user_id: number;
  employee_id: number;
  full_name: string;
  level: string;
  has_finance: boolean;
}

export const teamApi = {
  users: () => request<UsersScreen>('/api/platform/users'),
  /** Завести сотрудника без Telegram — только владелец. */
  addStaff: (fullName: string, positionId: number | null, allowDuplicate = false) =>
    request<{ employee_id: number; full_name: string }>('/api/platform/employees', {
      method: 'POST',
      body: { full_name: fullName, position_id: positionId, allow_duplicate: allowDuplicate },
    }),
  /** Коды официантов в кассе и то, кто за ними закреплён. */
  waiters: () => request<WaitersScreen>('/api/platform/waiters'),
  linkWaiter: (code: string, employeeId: number | null) =>
    request<{ code: string }>(`/api/platform/waiters/${encodeURIComponent(code)}`, {
      method: 'PUT',
      body: { employee_id: employeeId },
    }),
  /** Отметить должность официантской или снять отметку (D247). */
  setPositionIsWaiter: (positionId: number, isWaiter: boolean) =>
    request<{ position_id: number; is_waiter: boolean }>(
      `/api/platform/positions/${positionId}`,
      { method: 'PUT', body: { is_waiter: isWaiter } },
    ),
  setAccess: (userId: number, service: string, level: string) =>
    request<{ change: string }>(`/api/platform/users/${userId}/access`, {
      method: 'PUT',
      body: { service, access_level: level },
    }),
  revokeAccess: (userId: number, service: string) =>
    request<{ change: string }>(`/api/platform/users/${userId}/access/${service}`, {
      method: 'DELETE',
    }),
  setPosition: (userId: number, positionId: number | null) =>
    request<{ opened: string[] }>(`/api/platform/users/${userId}/position`, {
      method: 'PUT',
      body: { position_id: positionId },
    }),
  /** Право копировать и делать скриншоты (D233) — только владелец. */
  setCopy: (userId: number, allowed: boolean) =>
    request<{ copy_granted: boolean }>(`/api/platform/users/${userId}/copy`, {
      method: 'PUT',
      body: { allowed },
    }),
  /** Должность сотрудника без Telegram. */
  setStaffPosition: (employeeId: number, positionId: number | null) =>
    request<{ position_id: number | null }>(`/api/platform/employees/${employeeId}/position`, {
      method: 'PUT',
      body: { position_id: positionId },
    }),
  /** Уволить сотрудника без Telegram: входа у него нет, меняется статус. */
  fireStaff: (employeeId: number) =>
    request<{ status: string }>(`/api/platform/employees/${employeeId}/fire`, { method: 'POST' }),
  /** Увольнение одним действием (D231): статус, вход во все агенты, сессии. */
  fire: (userId: number) =>
    request<{ revoked: string[]; sessions: number }>(`/api/platform/users/${userId}/fire`, {
      method: 'POST',
    }),
  rights: () => request<RightsScreen>('/api/platform/rights'),
  userRights: (userId: number) => request<UserRights>(`/api/platform/users/${userId}/rights`),
  setUserRight: (
    userId: number,
    change: { service: string; object: string; action: string; granted: boolean | null },
  ) =>
    request<{ change: string }>(`/api/platform/users/${userId}/rights`, {
      method: 'PUT',
      body: change,
    }),
  resetUserRights: (userId: number, service: string) =>
    request<{ removed: number }>(
      `/api/platform/users/${userId}/rights?service=${encodeURIComponent(service)}`,
      { method: 'DELETE' },
    ),
  setPositionRight: (
    positionId: number,
    change: { service: string; object: string; action: string; granted: boolean },
  ) =>
    request<{ change: string }>(`/api/platform/positions/${positionId}/rights`, {
      method: 'PUT',
      body: change,
    }),
  renamePosition: (positionId: number, name: string, department?: string) =>
    request<{ position_id: number; name: string; department: string | null }>(
      `/api/platform/positions/${positionId}`,
      { method: 'PUT', body: department === undefined ? { name } : { name, department } },
    ),
  /** `deleted` — стёрта; `hidden` — на неё ссылались чек-листы или курсы, спрятана. */
  deletePosition: (positionId: number) =>
    request<{ position_id: number; result: 'deleted' | 'hidden' }>(
      `/api/platform/positions/${positionId}`,
      { method: 'DELETE' },
    ),
  createPosition: (name: string, copyFrom: number | null) =>
    request<{ position_id: number; name: string; rights_copied: number }>(
      '/api/platform/positions',
      { method: 'POST', body: { name, copy_from: copyFrom } },
    ),
  approve: (
    requestId: number,
    body: {
      services: string[];
      access_level: string;
      position_id: number | null;
      /** Сотрудник из справочника (D234); `null` — завести нового из анкеты. */
      employee_id?: number | null;
    },
  ) =>
    request<{ user_id: number }>(`/api/platform/access-requests/${requestId}/approve`, {
      method: 'POST',
      body,
    }),
  reject: (requestId: number) =>
    request<{ status: string }>(`/api/platform/access-requests/${requestId}/reject`, {
      method: 'POST',
      body: { reason: null },
    }),
  invite: () => request<{ how: string }>('/api/platform/invite'),
  money: () => request<{ people: MoneyPerson[] }>('/api/ops/portal/money'),
  setMoney: (employeeId: number, on: boolean) =>
    request<{ message: string; person: MoneyPerson }>(`/api/ops/portal/money/${employeeId}`, {
      method: 'PUT',
      body: { on },
    }),
};

/** Ключи данных раздела — одни на все экраны, чтобы правка обновляла всё. */
export const TEAM_KEYS = {
  users: ['team', 'users'] as const,
  waiters: ['team', 'waiters'] as const,
  rights: ['team', 'rights'] as const,
  userRights: (userId: number) => ['team', 'user-rights', userId] as const,
  money: ['team', 'money'] as const,
};

/**
 * Код официанта в кассе и то, кто это из наших.
 *
 * `till_name` — имя из самой кассы: оно приезжает каждые десять минут
 * вместе с кодом. `candidates` — подсказка по имени, не решение:
 * связывает человек (§7.3 ТЗ платформы, тёзки).
 */
export interface Waiter {
  /** Чем чек называет официанта — по нему связываем и его храним. */
  code: string;
  /** Номер станции: по нему официанта знают в ресторане. Его и показываем. */
  till_code: string | null;
  till_name: string | null;
  employee_id: number | null;
  employee_name: string | null;
  is_active: boolean;
  candidates: { employee_id: number; full_name: string; position_name: string | null; match: string }[];
}

export interface WaitersScreen {
  waiters: Waiter[];
}

export const SERVICE_ORDER: ServiceCode[] = ['ops', 'finance', 'study'];

export const SERVICE_NAMES: Record<string, string> = {
  ops: 'Операционный агент',
  finance: 'Финансы',
  study: 'Обучение',
};

export const LEVEL_NAMES: Record<string, string> = {
  user: 'сотрудник',
  admin: 'управляющий',
  host: 'владелец',
};

export const ACTION_NAMES: Record<string, string> = { view: 'видеть', edit: 'править' };

/** Откуда у человека право — словами (§2 правил интерфейса). */
export const SOURCE_NAMES: Record<string, string> = {
  host: 'владелец системы',
  owner: 'должность «Собственник»',
  admin: 'уровень «управляющий»',
  position: 'по должности',
  default: 'стартовый набор',
  access: 'вход в финансы даёт все разделы',
  added: 'лично',
};

const BOT_TITLES: Record<ServiceCode, string> = {
  ops: 'Операционный бот',
  study: 'Обучающий бот',
  finance: 'Финансовый бот',
};

/**
 * Ссылки на ботов — из настроек сервера (`GET /api/platform/navigation`).
 *
 * Адреса здесь не зашиты намеренно: до 20.09.2026 в этом файле стояли три
 * `t.me/toscana_*`, и второй ресторан получил бы приглашения в чужие боты.
 * Имя заказчика — настройка, а не строка в коде (D5).
 *
 * Бот, которого нет в настройках, в список не попадает: кнопка, ведущая в
 * никуда, хуже её отсутствия (§8 правил интерфейса).
 */
export function botLinks(
  bots: Partial<Record<ServiceCode, string>> | undefined,
): { service: ServiceCode; title: string; href: string }[] {
  const order: ServiceCode[] = ['ops', 'study', 'finance'];
  return order
    .filter((service) => Boolean(bots?.[service]))
    .map((service) => ({
      service,
      title: BOT_TITLES[service],
      href: `https://t.me/${bots![service]}`,
    }));
}

export function displayName(user: { full_name: string | null }): string {
  return user.full_name ?? 'Аккаунт без сотрудника';
}

/**
 * Текст отказа. У `ops-api` причина приходит словами; платформа её не
 * называет (§6.4) — тогда объясняем по коду.
 */
export function problemText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.detail) return error.detail;
    if (error.status === 403) return 'Не сохранилось: такое изменение может сделать только владелец.';
    if (error.status === 404) return 'Не нашлось — возможно, запись уже изменили. Обновите страницу.';
    if (error.status === 400) return 'Сервер не принял значение. Проверьте, что введено.';
  }
  return 'Не сохранилось. Попробуйте ещё раз.';
}
